import { LICENSE_PLANS, LICENSE_PLAN_FOOTNOTES, getLicensePlan, type LicensePlan } from '@responsedock/config';
import type { BillingTier } from './billing';

export const PLAN_TIER_ORDER: BillingTier[] = ['STARTER', 'PRO', 'BUSINESS', 'ENTERPRISE'];

/**
 * Feature switches per tier, mirroring the API's tier defaults (apps/api billing tierPlanDefaults).
 * Names, prices and quotas always come from LICENSE_PLANS in @responsedock/config.
 */
const TIER_FEATURES: Record<BillingTier, { sdk: boolean; webhooks: boolean; branding: boolean; customDomain: boolean }> = {
  STARTER: { sdk: false, webhooks: false, branding: false, customDomain: false },
  PRO: { sdk: true, webhooks: true, branding: true, customDomain: false },
  BUSINESS: { sdk: true, webhooks: true, branding: true, customDomain: true },
  ENTERPRISE: { sdk: true, webhooks: true, branding: true, customDomain: true },
};

export function tierFeatures(tier: BillingTier) {
  return TIER_FEATURES[tier];
}

export function tierRank(tier: BillingTier): number {
  return PLAN_TIER_ORDER.indexOf(tier);
}

export function planFor(tier: BillingTier): LicensePlan {
  return getLicensePlan(tier);
}

export function planName(tier: BillingTier): string {
  return getLicensePlan(tier).name;
}

/** The per-tier "aha" line (`tagline` in config), else the plan description. */
export function planAha(plan: LicensePlan): string {
  const tagline = (plan as LicensePlan & { tagline?: unknown }).tagline;
  return typeof tagline === 'string' && tagline.trim() ? tagline : plan.description;
}

/** "Everything in Pro, plus:" above a plan's own benefits, when config has one. */
export function planBenefitsIntro(plan: LicensePlan): string | null {
  const intro = (plan as LicensePlan & { benefitsIntro?: unknown }).benefitsIntro;
  return typeof intro === 'string' && intro.trim() ? intro : null;
}

/** Fine print under the plan cards, from config (absent in older config packages). */
export function planFootnotes(): string[] {
  return Array.isArray(LICENSE_PLAN_FOOTNOTES) ? LICENSE_PLAN_FOOTNOTES : [];
}

/** "$39 / month", "Free", "Custom pricing". */
export function planPriceLabel(plan: LicensePlan): string {
  const price = plan.price.trim();
  if (/^free$/i.test(price)) return 'Free';
  if (/^custom$/i.test(price)) return /request/i.test(plan.period) ? 'Price on request' : 'Custom pricing';
  const period = plan.period.trim().replace(/^per\s+/i, '');
  return period ? `${price} / ${period}` : price;
}

export function formatCount(value: number | null | undefined): string {
  if (value == null) return 'Unlimited';
  return new Intl.NumberFormat('en-US').format(value);
}

/** "10,000 responses/month", or "Custom responses" for unlimited plans. */
export function responsesPerMonthLabel(tier: BillingTier): string {
  const quota = getLicensePlan(tier).quotas.responsesPerMonth;
  return quota == null ? 'custom response volume' : `${formatCount(quota)} responses/month`;
}

/** Plans offered in the in-app picker (all configured plans, in tier order). */
export function pickerPlans(): LicensePlan[] {
  return PLAN_TIER_ORDER
    .map((tier) => LICENSE_PLANS.find((plan) => plan.tier === tier))
    .filter((plan): plan is LicensePlan => !!plan);
}

/** Everything the plan picker knows about the workspace to say what a plan change does. */
export interface WorkspacePlanContext {
  /** The tier the workspace runs on now (the trial tier during a trial). */
  currentTier: BillingTier;
  trialActive: boolean;
  responsesUsed: number;
  surveysCount: number | null;
  webhookCount: number | null;
  publicKeyCount: number | null;
  brandingCustomised: boolean;
  hidesPlatformBranding: boolean;
  customDomainSet: boolean;
  aiGenerationsUsed: number;
}

export interface PlanChange {
  tone: 'gain' | 'loss' | 'neutral';
  text: string;
}

function plural(count: number, one: string, many: string) {
  return `${formatCount(count)} ${count === 1 ? one : many}`;
}

/**
 * Workspace-specific "what changes" lines for moving to `target`, e.g.
 * "2 webhooks pause", "ResponseDock branding returns", "312 of 500 responses used this month".
 */
export function planChangeSummary(target: BillingTier, context: WorkspacePlanContext): PlanChange[] {
  const changes: PlanChange[] = [];
  const now = TIER_FEATURES[context.currentTier];
  const next = TIER_FEATURES[target];
  const nextPlan = getLicensePlan(target);
  const currentPlan = getLicensePlan(context.currentTier);
  const responsesLimit = nextPlan.quotas.responsesPerMonth;

  if (target === context.currentTier) {
    if (context.trialActive) changes.push({ tone: 'neutral', text: 'Keeps everything you use in your trial today.' });
    else changes.push({ tone: 'neutral', text: 'Your current plan.' });
  }

  if (now.webhooks && !next.webhooks && (context.webhookCount ?? 0) > 0) {
    changes.push({ tone: 'loss', text: `${plural(context.webhookCount!, 'webhook pauses', 'webhooks pause')}` });
  } else if (!now.webhooks && next.webhooks) {
    changes.push({ tone: 'gain', text: 'Webhooks and the Zapier app unlock' });
  }

  if (now.sdk && !next.sdk && (context.publicKeyCount ?? 0) > 0) {
    changes.push({ tone: 'loss', text: context.publicKeyCount === 1 ? 'Your SDK key stops loading surveys in your product' : `${formatCount(context.publicKeyCount)} SDK keys stop loading surveys in your product` });
  } else if (!now.sdk && next.sdk) {
    changes.push({ tone: 'gain', text: 'SDK keys unlock: embed surveys inside your product' });
  }

  // Logo and colours work on every plan; white-label (hiding ResponseDock) is what changes.
  if (now.branding && !next.branding) {
    if (context.hidesPlatformBranding) changes.push({ tone: 'loss', text: '“Powered by ResponseDock” returns on your surveys' });
  } else if (!now.branding && next.branding) {
    changes.push({ tone: 'gain', text: 'White-label: hide ResponseDock branding' });
  }

  if (now.customDomain && !next.customDomain && context.customDomainSet) {
    changes.push({ tone: 'loss', text: 'Your custom domain stops serving surveys' });
  } else if (!now.customDomain && next.customDomain) {
    changes.push({ tone: 'gain', text: 'Custom domain for your surveys' });
  }

  if (responsesLimit == null) {
    changes.push({ tone: 'gain', text: 'Custom response volume' });
  } else {
    const over = context.responsesUsed > responsesLimit;
    changes.push({
      tone: over ? 'loss' : 'neutral',
      text: `${formatCount(context.responsesUsed)} of ${formatCount(responsesLimit)} responses used this month${over ? ' — collection pauses' : ''}`,
    });
  }

  const surveysLimit = nextPlan.quotas.surveys;
  if (surveysLimit != null && context.surveysCount != null && context.surveysCount > surveysLimit) {
    changes.push({ tone: 'loss', text: `${formatCount(context.surveysCount)} of ${formatCount(surveysLimit)} surveys: no new surveys until you archive some` });
  }

  const aiLimit = nextPlan.quotas.aiGenerationsPerMonth;
  const currentAiLimit = currentPlan.quotas.aiGenerationsPerMonth;
  if (aiLimit !== currentAiLimit && target !== context.currentTier) {
    changes.push({
      tone: aiLimit == null || (currentAiLimit != null && aiLimit > currentAiLimit) ? 'gain' : 'loss',
      text: aiLimit == null ? 'Custom AI survey generations' : `${formatCount(aiLimit)} AI survey generations / month`,
    });
  }

  return changes;
}
